import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
} from "reactstrap";
import { ClientAddressDTO } from "../../models/ClientAddress/ClientAdressDTO";
import { ClientAddressService } from "../../services/ClientAddressService";
import { CountryService } from "../../services/CountryService";
import Toast from "../../helpers/Toast";
import ButtonComponent from "../ButtonComponent";
import { useIntl } from "react-intl";
import SearchSelectComponent, { SelectItemsProps } from "../SearchSelectComponent";

type Props = {
  visible: boolean;
  onClose: () => void;
  address: ClientAddressDTO | null;
  onUpdated: () => void;
};

export default function EditAddressPopup({
  visible,
  onClose,
  address,
  onUpdated,
}: Props) {
  const intl = useIntl();
  const addressService = new ClientAddressService();
  const countryService = new CountryService();

  const [formData, setFormData] = useState<ClientAddressDTO>({} as ClientAddressDTO);
  const [countrySelect, setCountrySelect] = useState<SelectItemsProps | null>(null);

  useEffect(() => {
    if (address) {
      setFormData({
        ...address,
        active: address.active ?? false,
      });

      if (address.countryId) {
        loadCountryById(address.countryId);
      }
    }
  }, [address]);

  const loadCountryById = async (id: number) => {
    try {
      const result = await countryService.GetCountryById(id);
      if (result.success && result.obj) {
        setCountrySelect({
          value: result.obj.id.toString(),
          label: result.obj.name,
        });
      }
    } catch {
      Toast.Show("error", intl.formatMessage({ id: "Screens.ClientAddresses.ErrorFetchingCountries" }));
    }
  };

  const searchCountry = async (input: string): Promise<SelectItemsProps[]> => {
    const result = await countryService.SearchCountryByName(input);
    if (!result.success || !result.obj) {
      Toast.Show("error", result.message);
      return [];
    }

    return result.obj.map((c) => ({
      value: c.id.toString(),
      label: c.name,
    }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    if (!countrySelect || !countrySelect.value) {
      Toast.Show("error", intl.formatMessage({ id: "Screens.ClientAddresses.Validation.CountryRequired" }));
      return;
    }

    try {
      const updatedData = {
        ...formData,
        countryId: parseInt(countrySelect.value),
      };

      const result = await addressService.update(updatedData);
      if (result.success) {
        Toast.Show("success", intl.formatMessage({ id: "Screens.ClientAddresses.UpdatedSuccess" }));
        onUpdated();
        onClose();
      } else {
        Toast.Show("error", intl.formatMessage({ id: "Screens.ClientAddresses.UpdatedError" }));
      }
    } catch (err) {
      Toast.Show("error", intl.formatMessage({ id: "Screens.ClientAddresses.UpdatedError" }));
    }
  };

  const handleToggleActive = async () => {
    if (!formData.id) return;

    try {
      const result = formData.active
        ? await addressService.deactivate(formData.id)
        : await addressService.activate(formData.id);

      if (result.success) {
        Toast.Show("success", intl.formatMessage({ id: "Screens.ClientAddresses.UpdatedSuccess" }));
        setFormData({ ...formData, active: !formData.active });
        onUpdated();
      } else {
        Toast.Show("error", intl.formatMessage({ id: "Screens.ClientAddresses.UpdatedError" }));
      }
    } catch {
      Toast.Show("error", intl.formatMessage({ id: "Screens.ClientAddresses.UpdatedError" }));
    }
  };

  if (!address) return null;

  return (
    <Modal isOpen={visible} toggle={onClose} size="lg">
      <ModalHeader toggle={onClose}>
        {intl.formatMessage({ id: "Screens.ClientAddresses.EditTitle" })}
      </ModalHeader>
      <ModalBody>
        <Form>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>Rua 1</Label>
                <Input name="street1" value={formData.street1 || ""} onChange={handleChange} />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Rua 2</Label>
                <Input name="street2" value={formData.street2 || ""} onChange={handleChange} />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md={4}>
              <FormGroup>
                <Label>Código Postal</Label>
                <Input name="postalCode" value={formData.postalCode || ""} onChange={handleChange} />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label>Cidade</Label>
                <Input name="city" value={formData.city || ""} onChange={handleChange} />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <SearchSelectComponent
                  xl={12}
                  lg={12}
                  md={12}
                  sm={12}
                  xs={12}
                  label={intl.formatMessage({ id: "Screens.ClientAddresses.SelectCountry" })}
                  loadOptions={searchCountry}
                  onChange={(e: SelectItemsProps) => setCountrySelect(e)}
                  value={countrySelect}
                  required
                  style={{ width: "100%" }}
                />
              </FormGroup>
            </Col>
          </Row>
        </Form>
      </ModalBody>
      <ModalFooter>
        <ButtonComponent text="Cancelar" onClick={onClose} />
        <ButtonComponent
          text={formData.active ? "Desativar" : "Ativar"}
          onClick={handleToggleActive}
          style={{ minWidth: "120px", marginRight: "10px" }}
        />
        <ButtonComponent text="Guardar" onClick={handleSubmit} style={{ minWidth: "100px" }} />
      </ModalFooter>
    </Modal>
  );
}
